import type {
  AdminCustomerCart,
  ApiContext,
  ApiNotAuthorized,
  CartItemNotFound,
  CheckoutRefused,
  CustomerCart,
  CustomerCartItemEdit,
  CustomerCartNotFound,
  InvalidFormField,
  PaymentsNotReady,
  ScansNotPurchasable,
  StoreNotFound,
  TerminalCheckout,
} from "@hobbyshopify/backend-client";
import type { AsyncResult } from "operation-result.js";
import type { AuthHandler } from "trunk.js";
import { shopEntryId } from "../primitives/workspace";
import type { PendingCartsRepository } from "../repositories/pending-carts-repository";
import type { BackendApi } from "./api";

/**
 * Puts a customer's confirmed scan in their open cart, or opens one for it.
 * Opening takes the plain store id: the cart's counter comes from the scan.
 */
export async function addScanToCart(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  pendingCartsRepository: PendingCartsRepository;
  shopId: string;
  customerId: string;
  scanId: string;
  openCartId: string | null;
}): AsyncResult<
  unknown,
  ApiNotAuthorized | StoreNotFound | CustomerCartNotFound | CheckoutRefused | ScansNotPurchasable | InvalidFormField
> {
  const cartId = o.openCartId;
  const r = cartId
    ? await o.authHandler.callApi((ctx) => o.client.customerCart.addItems(ctx, cartId, [o.scanId]))
    : await o.authHandler.callApi((ctx) => o.client.customerCart.adminCreate(ctx, o.shopId, o.customerId, [o.scanId]));
  if (r.ok) o.pendingCartsRepository.markStale();
  return r;
}

/** Takes one line out; the last line deletes the cart, since the backend refuses to empty one. */
export function removeLineFromCart(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  pendingCartsRepository: PendingCartsRepository;
  cart: AdminCustomerCart;
  itemId: string;
}): AsyncResult<unknown, ApiNotAuthorized | CustomerCartNotFound | CheckoutRefused | CartItemNotFound> {
  if (o.cart.items.length <= 1) return removeCart({ ...o, customerCartId: o.cart.id });
  return removeCartItem({ ...o, customerCartId: o.cart.id, itemId: o.itemId });
}

/** Every cart at the counter, newest first. */
export function loadCarts(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  locationId: string | null;
}): AsyncResult<AdminCustomerCart[], ApiNotAuthorized | StoreNotFound> {
  return o.authHandler.callApi((ctx) => o.client.customerCart.adminList(ctx, shopEntryId(o.shopId, o.locationId)));
}

export async function editCartItem(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  pendingCartsRepository: PendingCartsRepository;
  customerCartId: string;
  itemId: string;
  edit: CustomerCartItemEdit;
}): AsyncResult<
  CustomerCart,
  ApiNotAuthorized | CustomerCartNotFound | CheckoutRefused | CartItemNotFound | InvalidFormField
> {
  const r = await o.authHandler.callApi((ctx) =>
    o.client.customerCart.editItem(ctx, o.customerCartId, o.itemId, o.edit),
  );
  if (r.ok) o.pendingCartsRepository.markStale();
  return r;
}

export async function removeCartItem(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  pendingCartsRepository: PendingCartsRepository;
  customerCartId: string;
  itemId: string;
}): AsyncResult<CustomerCart, ApiNotAuthorized | CustomerCartNotFound | CheckoutRefused | CartItemNotFound> {
  const r = await o.authHandler.callApi((ctx) => o.client.customerCart.removeItem(ctx, o.customerCartId, o.itemId));
  if (r.ok) o.pendingCartsRepository.markStale();
  return r;
}

/** Deletes an open cart; the badge is refetched since one fewer waits on the merchant. */
export async function removeCart(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  pendingCartsRepository: PendingCartsRepository;
  customerCartId: string;
}): AsyncResult<void, ApiNotAuthorized | CustomerCartNotFound | CheckoutRefused> {
  const r = await o.authHandler.callApi((ctx) => o.client.customerCart.remove(ctx, o.customerCartId));
  if (r.ok) o.pendingCartsRepository.markStale();
  return r;
}

/** Prepares an in-person payment: the card-present PaymentIntent the Terminal SDK collects against. */
export function startPosCheckout(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  customerCartId: string;
}): AsyncResult<TerminalCheckout, ApiNotAuthorized | CustomerCartNotFound | CheckoutRefused | PaymentsNotReady> {
  return o.authHandler.callApi((ctx) => o.client.stripe.posCheckout(ctx, o.customerCartId));
}
